/**
 * The settings registry — one table per settings section, and everything about
 * a setting derived from it: its default, how a stored value is read, what a
 * save may contain, and the field the Settings page shows. Before this, those
 * lived in three places (the server's resolvers, the dashboard's field tables,
 * nothing at all on save) that disagreed in 40 ways (2026-10-06, roadmap 5b).
 *
 * A section's table is the only place a setting is declared. Adding one is a
 * row here; the save contract, the reader, the defaults and the Settings field
 * follow. A setting nothing reads does not belong here.
 */
export interface NumberSetting {
  readonly type: 'number';
  readonly default: number;
  /** Smallest allowed value (stored units). */
  readonly min: number;
  /** Largest allowed value (stored units); absent = no upper bound. */
  readonly max?: number;
  /** Whole numbers only. */
  readonly integer: boolean;
  readonly label: string;
  readonly hint: string;
  /** Unit shown next to the field (after `scale`). */
  readonly unit?: string;
  /** Stored units per displayed unit (e.g. ms per hour). Display = stored / scale. */
  readonly scale?: number;
  /** Display step for decimal settings. */
  readonly step?: number;
  /** Read only by the shared agent loop / API providers — API-key and cloud
   *  turns. Subscription (Claude CLI) turns don't use it (MA-386 C). */
  readonly apiKeyOnly?: true;
}

export interface EnumSetting<V extends string = string> {
  readonly type: 'enum';
  readonly default: V;
  readonly values: readonly V[];
  readonly label: string;
  readonly hint: string;
}

export interface BooleanSetting {
  readonly type: 'boolean';
  readonly default: boolean;
  readonly label: string;
  readonly hint: string;
}

export interface ListSetting<V extends string = string> {
  readonly type: 'list';
  readonly default: readonly V[];
  /** Allowed members. Absent: any plain name ({@link isPlainName}) — e.g. folder names. */
  readonly values?: readonly V[];
  readonly label: string;
  readonly hint: string;
}

export type Setting = NumberSetting | EnumSetting | BooleanSetting | ListSetting;
export type SettingsSection = Readonly<Record<string, Setting>>;

type ValueOf<S extends Setting> = S extends NumberSetting
  ? number
  : S extends EnumSetting<infer V>
    ? V
    : S extends BooleanSetting
      ? boolean
      : S extends ListSetting<infer V>
        ? V[]
        : never;

/** The resolved values of a section — every setting present. */
export type SectionValues<T extends SettingsSection> = { -readonly [K in keyof T]: ValueOf<T[K]> };

function isValid(setting: Setting, value: unknown): boolean {
  switch (setting.type) {
    case 'number':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        (!setting.integer || Number.isInteger(value)) &&
        value >= setting.min &&
        (setting.max === undefined || value <= setting.max)
      );
    case 'enum':
      return typeof value === 'string' && (setting.values as readonly string[]).includes(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'list':
      return (
        Array.isArray(value) &&
        value.every(
          (v) =>
            typeof v === 'string' &&
            (setting.values ? (setting.values as readonly string[]).includes(v) : isPlainName(v)),
        )
      );
  }
}

/** A non-empty name with no path separators and no `.`/`..` — what a free list (no `values`) holds. */
export function isPlainName(value: string): boolean {
  return value.length > 0 && value.length <= 255 && !/[/\\\0]/.test(value) && value !== '.' && value !== '..';
}

/** Every setting of a section at its default. */
export function defaultsOf<T extends SettingsSection>(section: T): SectionValues<T> {
  const out: Record<string, unknown> = {};
  for (const [key, setting] of Object.entries(section)) {
    out[key] = setting.type === 'list' ? [...setting.default] : setting.default;
  }
  return out as SectionValues<T>;
}

/**
 * Read a stored section: each setting's stored value when it is valid, its
 * default otherwise (missing, wrong type, out of range). Saves are checked
 * against the same rules (`sectionPatchSchema`, ./contract.ts), so an invalid stored
 * value only comes from a hand edit of the file — it never reaches a consumer.
 */
export function resolveSection<T extends SettingsSection>(section: T, stored: unknown): SectionValues<T> {
  const out = defaultsOf(section) as Record<string, unknown>;
  if (stored === null || typeof stored !== 'object') return out as SectionValues<T>;
  const raw = stored as Record<string, unknown>;
  for (const [key, setting] of Object.entries(section)) {
    if (isValid(setting, raw[key])) out[key] = setting.type === 'list' ? [...(raw[key] as string[])] : raw[key];
  }
  return out as SectionValues<T>;
}

/** A numeric field as the Settings page renders it. */
export interface SettingField {
  key: string;
  label: string;
  hint: string;
  /** Default in stored units. */
  default: number;
  /** Bounds in displayed units. */
  min: number;
  max?: number;
  integer: boolean;
  unit?: string;
  /** Stored units per displayed unit. */
  scale?: number;
  step?: number;
  /** Not used on subscription (Claude CLI) turns — see `NumberSetting.apiKeyOnly`. */
  apiKeyOnly?: true;
}

/** The numeric settings of a section, in table order, for the Settings page. */
export function numberFields(section: SettingsSection): SettingField[] {
  const out: SettingField[] = [];
  for (const [key, s] of Object.entries(section)) {
    if (s.type !== 'number') continue;
    const scale = s.scale ?? 1;
    out.push({
      key,
      label: s.label,
      hint: s.hint,
      default: s.default,
      min: s.min / scale,
      ...(s.max !== undefined ? { max: s.max / scale } : {}),
      integer: s.integer,
      ...(s.unit ? { unit: s.unit } : {}),
      ...(s.scale ? { scale: s.scale } : {}),
      ...(s.step ? { step: s.step } : {}),
      ...(s.apiKeyOnly ? { apiKeyOnly: true as const } : {}),
    });
  }
  return out;
}
